import { createSelector } from 'reselect';
import memoize from '@doveconviene/sf-web-js-sdk/memoize';
import { formatGeolocation, parseLocationSearch } from 'utils';
import isFromExtendedNetworkUtil from 'utils/isFromExtendedNetwork';
import { selectRouterInitialLocation } from 'flux/selectors/router.selectors';
import { BASIC_THEME_TWO_PAGES_BREAKPOINT_FALLBACK } from 'basicTheme';

export const selectProperty = (state) => state.property;
export const selectPropertyId = (state) => state.property.id;
export const selectPropertyFetched = (state) => state.property.fetched;
export const selectPropertyExtras = (state) => state.property.extras || {};
export const selectPropertyAssets = (state) => state.property.assets || [];

export const selectPropertyMessages = createSelector(selectPropertyExtras, (extras) => {
  const { messages } = extras;
  return messages || {};
});

export const selectPropertyFBPixels = createSelector(selectPropertyExtras, (extras) => {
  const { REACT_APP_ENV, REACT_APP_FB_PIXEL_ID } = process.env;
  const pixels =
    REACT_APP_ENV === 'dev' || REACT_APP_ENV === 'stg'
      ? [REACT_APP_FB_PIXEL_ID]
      : extras.fb_pixels || [];

  return pixels.filter((pixel) => !!pixel);
});

export const selectPropertyGoogleAnalytics = createSelector(
  selectPropertyExtras,
  ({ google_analytics: googleAnalytics, google_analytics_ids: googleAnalyticsIds = [] }) => {
    const { REACT_APP_ENV, REACT_APP_GOOGLE_ANALYTICS_ID } = process.env;
    return REACT_APP_ENV === 'dev' || REACT_APP_ENV === 'stg'
      ? [REACT_APP_GOOGLE_ANALYTICS_ID, '']
      : [...googleAnalyticsIds, googleAnalytics];
  },
);

export const selectPropertyGoogleAdWords = createSelector(selectPropertyExtras, (extras) => {
  const { REACT_APP_ENV, REACT_APP_GOOGLE_ADWORDS_ID } = process.env;
  return REACT_APP_ENV === 'dev' || REACT_APP_ENV === 'stg'
    ? REACT_APP_GOOGLE_ADWORDS_ID
    : extras.google_adwords;
});

export const selectPropertyGoogleAdWordsLabel = createSelector(selectPropertyExtras, (extras) => {
  const { REACT_APP_ENV, REACT_APP_GOOGLE_ADWORDS_LABEL } = process.env;
  return REACT_APP_ENV === 'dev' || REACT_APP_ENV === 'stg'
    ? REACT_APP_GOOGLE_ADWORDS_LABEL
    : extras.google_adwords_label;
});

export const selectPropertyGoogleAdWordsPhoneCta = createSelector(
  selectPropertyExtras,
  (extras) => {
    const { REACT_APP_ENV, REACT_APP_GOOGLE_ADWORDS_LABEL } = process.env;
    return REACT_APP_ENV === 'dev' || REACT_APP_ENV === 'stg'
      ? REACT_APP_GOOGLE_ADWORDS_LABEL
      : extras.google_adwords_phone_call;
  },
);

export const selectPropertyWoosmapPublicKey = createSelector(selectPropertyExtras, (extras) => {
  const { REACT_APP_ENV, REACT_APP_WOOSMAP_PUBLIC_KEY } = process.env;
  return REACT_APP_ENV === 'dev' || REACT_APP_ENV === 'stg'
    ? REACT_APP_WOOSMAP_PUBLIC_KEY
    : extras.woosmap_public_key;
});

export const selectPropertyGAWPercentageLabels = createSelector(selectPropertyExtras, (extras) => {
  const { REACT_APP_ENV } = process.env;
  return REACT_APP_ENV === 'dev' || REACT_APP_ENV === 'stg'
    ? {
        30: process.env.REACT_APP_GAW_PERCENTAGE_30_LABEL,
        50: process.env.REACT_APP_GAW_PERCENTAGE_50_LABEL,
        80: process.env.REACT_APP_GAW_PERCENTAGE_80_LABEL,
      }
    : {
        30: extras.google_adwords_percentage_30_label,
        50: extras.google_adwords_percentage_50_label,
        80: extras.google_adwords_percentage_80_label,
      };
});

export const selectPropertyGAWPercentageLabel = createSelector(
  [(_, percentage) => percentage, selectPropertyGAWPercentageLabels],
  (percentage, labels) => labels[percentage] || null,
);

export const selectPropertyGeolocation = (state) => {
  const { lat, lng, label } = selectPropertyExtras(state).geolocation;

  return lat && lng && label ? formatGeolocation({ lat, lng, label }, 'property') : null;
};

export const selectPropertyCTA = memoize((state, index) => {
  const rawCTA = state.property.ctas[index];
  if (!rawCTA) return null;

  const {
    name,
    title,
    description,
    label,
    href,
    assets,
    is_active, // eslint-disable-line camelcase
  } = rawCTA;

  return {
    name,
    title,
    description,
    button: { label, href },
    assets: assets.reduce((acc, asset) => ({ ...acc, [asset.name]: asset.url }), {}),
    is_active,
  };
});

export const selectPropertyExternals = (state, key) =>
  state.property.extras[key] || {
    active: true,
    url: '',
    target: '',
  };

export const selectPropertyEnrichmentPins = createSelector(selectPropertyAssets, (assets) => {
  const pinCrop = assets.find(({ name }) => name === 'pinCrop');
  const pinExternalLink = assets.find(({ name }) => name === 'pinExternalLink');

  return {
    crop: pinCrop ? pinCrop.url : null,
    external_link: pinExternalLink ? pinExternalLink.url : null,
  };
});

export const selectPropertyPrivacyUrl = createSelector(selectPropertyExtras, (extras) => {
  const {
    web_network: {
      privacy_policy_url: privacyPolicyUrl = null,
      privacy_policy_url_label: privacyPolicyUrlLabel = null,
    } = {},
  } = extras;
  return (
    (privacyPolicyUrl &&
      privacyPolicyUrlLabel && {
        label: privacyPolicyUrlLabel,
        url: privacyPolicyUrl,
      }) ||
    null
  );
});

export const selectPropertyCloseCta = createSelector(selectPropertyExtras, (extras) => {
  const { close_cta: closeCta = null } = extras;
  return closeCta;
});

export const selectPropertyCloseCtaBack = createSelector(selectPropertyExtras, (extras) => {
  const { close_cta_back: closeCtaBack = false } = extras;

  return Boolean(+closeCtaBack);
});

export const selectIsProfilingConsent = (state) => {
  const { profiling_consent: profilingConsent } = selectPropertyExtras(state);
  return profilingConsent;
};

export const selectPropertyMultiRetailerMode = createSelector(selectPropertyExtras, (extras) =>
  Boolean(+extras?.multiretailer_mode),
);

export const selectPropertyMultiStoreMode = createSelector(selectPropertyExtras, (extras) =>
  Boolean(+extras?.multistore_mode),
);

export const selectPropertyEndDateHidden = createSelector(selectPropertyExtras, (extras) =>
  Boolean(+extras?.hidden_end_date),
);

export const isStoreLocatorActive = createSelector(selectPropertyExtras, (extras) =>
  Boolean(+extras?.store?.active),
);

export const isFromExtendedNetwork = createSelector(selectRouterInitialLocation, (location) =>
  isFromExtendedNetworkUtil(location.search),
);

export const selectDataLayerIsActive = createSelector(selectPropertyExtras, (extras) =>
  Boolean(+extras?.data_layer_is_active),
);

export const selectCtaTextOverride = createSelector(
  selectPropertyExtras,
  (extras) => extras?.cta_text_override,
);

export const selectCtaUtmParamsReplacementEnabled = createSelector(selectPropertyExtras, (extras) =>
  Boolean(+extras?.cta_utm_params_replacement_enabled),
);

export const selectAddToCartCtaEnabled = createSelector(selectPropertyExtras, (extras) =>
  Boolean(+extras?.cta_add_to_cart_enabled),
);

export const selectAddToCartCtaText = createSelector(
  selectPropertyExtras,
  (extras) => extras?.cta_add_to_cart_text || '',
);

export const selectAddToCartTextColor = createSelector(
  selectPropertyExtras,
  (extras) => extras?.cta_add_to_cart_text_color || 'white',
);

export const selectAddToCartBackgroundColor = createSelector(
  selectPropertyExtras,
  (extras) => extras?.cta_add_to_cart_background_color || 'black',
);

export const selectMinWidthToShowTwoPages = createSelector(selectPropertyExtras, (extras) => {
  const breakpointString = extras.two_pages_breakpoint;
  if (breakpointString === null || breakpointString === undefined)
    return BASIC_THEME_TWO_PAGES_BREAKPOINT_FALLBACK;
  if (typeof breakpointString === 'string' && breakpointString.trim() === '')
    return BASIC_THEME_TWO_PAGES_BREAKPOINT_FALLBACK;

  const breakpointNumber = Number(breakpointString);

  const isValidNumber = !isNaN(breakpointNumber);
  if (!isValidNumber) return BASIC_THEME_TWO_PAGES_BREAKPOINT_FALLBACK;

  return breakpointNumber;
});

/**
 * Names of `extras` fields (GET .../property) forwarded to the FlyerSA Next iframe as query params.
 * Values are URL-encoded: primitives via `String(value)`, objects and arrays as `JSON.stringify`.
 * Add or remove entries here when new fields should cross the iframe boundary.
 */
export const NEXT_IFRAME_EXTRAS_KEYS_TO_FORWARD = ['hide_header_next'];

function isHideHeaderNextExtraEnabled(value) {
  if (value === true || value === 1 || value === '1') return true;
  if (typeof value === 'string' && value.toLowerCase() === 'true') return true;
  return false;
}

export const selectHideHeaderNextEnabled = createSelector(selectPropertyExtras, (extras) =>
  isHideHeaderNextExtraEnabled(extras?.hide_header_next),
);

function isShowFlyerGibsExtraEnabled(value) {
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === '0' || normalized === 'false') return false;
  }
  return true;
}

/** Initial query string params; `{}` when the state has no router (e.g. the splash rendered before the store). */
const selectInitialQueryParams = createSelector(
  (state) => state.router?.initialLocation,
  (initialLocation) => (initialLocation ? parseLocationSearch(initialLocation) : {}),
);

/** A query string param repeated in the URL is parsed as an array: the last occurrence wins. */
const lastQueryValue = (value) => (Array.isArray(value) ? value[value.length - 1] : value);

/** Property extra `hide_thumbnail_on_flyer_loading` (super admin): saved as a boolean, legacy `'1'` / `'true'` accepted too. */
function isHideThumbnailOnFlyerLoadingExtraEnabled(value) {
  if (value === true || value === 1 || value === '1') return true;
  if (typeof value === 'string' && value.trim().toLowerCase() === 'true') return true;
  return false;
}

/**
 * When true, the flyer first-page thumbnail is not shown while the flyer is loading: only the property logo is shown.
 * Enabled by the property extra or by the `?hideThumbnailOnFlyerLoading=true` override.
 */
export const selectHideThumbnailOnFlyerLoading = createSelector(
  [selectPropertyExtras, selectInitialQueryParams],
  (extras, params) =>
    isHideThumbnailOnFlyerLoadingExtraEnabled(extras?.hide_thumbnail_on_flyer_loading) ||
    isHideThumbnailOnFlyerLoadingExtraEnabled(lastQueryValue(params.hideThumbnailOnFlyerLoading)),
);

export const selectPropertyLogoUrl = createSelector(
  selectPropertyAssets,
  (assets) => assets.find((asset) => asset.name === 'logo')?.url || null,
);

/** Property extra `pinchable_viewer` (super admin): saved as a boolean, legacy `'1'` / `'true'` accepted too. */
function isPinchableViewerExtraEnabled(value) {
  if (value === true || value === 1 || value === '1') return true;
  if (typeof value === 'string' && value.trim().toLowerCase() === 'true') return true;
  return false;
}

/** When true, pinch-to-zoom is enabled on the flyer viewer for this property. */
export const selectPropertyPinchableViewer = createSelector(selectPropertyExtras, (extras) =>
  isPinchableViewerExtraEnabled(extras?.pinchable_viewer),
);

export const DEFAULT_VIEWER_MAX_SCALE = 2;

/** Integer >= 1 (number or numeric string), otherwise `null`. */
function parseViewerMaxScale(raw) {
  if (typeof raw !== 'number' && typeof raw !== 'string') return null;
  const value = Number(raw);
  return Number.isInteger(value) && value >= 1 ? value : null;
}

/**
 * Max zoom of the flyer viewer, e.g. 4 = zoom up to 4x: `?viewerMaxScale=` override first, then the property extra
 * `viewer_max_scale` (super admin), then the viewer default (2x). Values that are not integers >= 1 are ignored.
 */
export const selectViewerMaxScale = createSelector(
  [selectPropertyExtras, selectInitialQueryParams],
  (extras, params) =>
    parseViewerMaxScale(lastQueryValue(params.viewerMaxScale)) ??
    parseViewerMaxScale(extras?.viewer_max_scale) ??
    DEFAULT_VIEWER_MAX_SCALE,
);

export const selectShowFlyerGibs = createSelector(selectPropertyExtras, (extras) =>
  isShowFlyerGibsExtraEnabled(extras?.show_flyer_gibs),
);

function extraValueToEncodedQueryValue(value) {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'object') {
    try {
      return encodeURIComponent(JSON.stringify(value));
    } catch {
      return null;
    }
  }
  return encodeURIComponent(String(value));
}

/** @returns {string[]} e.g. ['hide_header_next=true'] — append each as `&${segment}` */
export const selectPropertyNextIframeQuerySegments = createSelector(
  selectPropertyExtras,
  (extras) => {
    const segments = [];
    for (const key of NEXT_IFRAME_EXTRAS_KEYS_TO_FORWARD) {
      if (!Object.prototype.hasOwnProperty.call(extras, key)) continue;
      const encoded = extraValueToEncodedQueryValue(extras[key]);
      if (encoded === null) continue;
      segments.push(`${encodeURIComponent(key)}=${encoded}`);
    }
    return segments;
  },
);
